import React, { useState, useEffect, useMemo } from 'react';
import { Search, RotateCcw, FileText, UploadCloud, X, Filter, User, Building2, Printer } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import Select from 'react-select';
import toast from 'react-hot-toast';
import api from '../services/api.js';
import { useAuth } from '../App.jsx';
import IndiceDocumentosModal from '../components/IndiceDocumentosModal.jsx';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { handleImprimirIndice } from '../utils/printIndicePropiedad.js';

/**
 * IndicePropiedad — Buscador del Índice de Propiedades.
 *
 * adaptada al diseño midnight/copper del sistema.
 *
 * Campos de búsqueda: Apellido Paterno / Razón Social, Apellido Materno, Primer Nombre.
 * Resultados: tabla paginada con los datos del acto registral.
 */
export default function IndicePropiedad() {
  const FILTROS_VACIOS = { apellido_paterno: '', apellido_materno: '', primer_nombre: '', tipo_registro: '', naturaleza_id: '' };

  const [tipoPersona, setTipoPersona] = useState('persona'); // 'persona' | 'empresa'
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);
  const [naturalezas, setNaturalezas] = useState([]);
  const [filtroRefinar, setFiltroRefinar] = useState('');
  const [resultados, setResultados] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [buscado, setBuscado] = useState(false);

  const { user, isStaff, isAdmin } = useAuth();
  const [modalRegistro, setModalRegistro] = useState(null);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const location = useLocation();

  // Modal de opciones de impresión (solo para usuarios autenticados)
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [imprimiendoTodo, setImprimiendoTodo] = useState(false);

  // Paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const POR_PAGINA = 20;

  useEffect(() => {
    api.get('/indices-naturalezas')
      .then(res => {
        if (Array.isArray(res.data)) {
          setNaturalezas(res.data);
        }
      })
      .catch(err => console.error('Error al cargar catálogo de naturalezas:', err));
  }, []);

  const opcionesNaturalezas = useMemo(() => {
    return naturalezas.map(nat => ({
      value: String(nat.id),
      label: `${nat.descripcion}${nat.codigo_antiguo ? ` (${nat.codigo_antiguo})` : ''}`
    }));
  }, [naturalezas]);

  const selectNaturalezaStyles = useMemo(() => ({
    control: (base, state) => ({
      ...base,
      borderRadius: '8px',
      borderColor: state.isFocused ? 'var(--primary, #003366)' : '#cbd5e1',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(0, 51, 102, 0.15)' : 'none',
      minHeight: '42px',
      fontSize: '0.9rem',
      backgroundColor: '#ffffff',
      '&:hover': {
        borderColor: 'var(--primary, #003366)'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 10px',
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: 'inherit',
      border: 'none',
      boxShadow: 'none',
      '&:focus': {
        outline: 'none',
        border: 'none',
        boxShadow: 'none'
      }
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({
      ...base,
      borderRadius: '8px',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      zIndex: 9999,
      overflow: 'hidden'
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: '260px'
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '0.85rem',
      cursor: 'pointer',
      padding: '8px 12px',
      backgroundColor: state.isSelected 
        ? 'var(--primary, #003366)' 
        : state.isFocused 
          ? '#eff6ff' 
          : 'transparent',
      color: state.isSelected ? '#ffffff' : state.isFocused ? '#1e40af' : '#1e293b',
      '&:active': {
        backgroundColor: 'var(--primary, #003366)',
        color: '#ffffff'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: '#94a3b8',
      fontSize: '0.85rem'
    }),
    singleValue: (base) => ({
      ...base,
      color: '#1e293b',
      fontWeight: '500',
      fontSize: '0.88rem'
    }),
    clearIndicator: (base) => ({
      ...base,
      cursor: 'pointer',
      padding: '4px 6px',
      color: '#94a3b8',
      '&:hover': { color: '#ef4444' }
    }),
    dropdownIndicator: (base) => ({
      ...base,
      cursor: 'pointer',
      padding: '4px 8px',
      color: '#64748b'
    })
  }), []);

  const handleChange = (e) =>
    setFiltros((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSwitchTipo = (nuevoTipo) => {
    setTipoPersona(nuevoTipo);
    if (nuevoTipo === 'empresa') {
      // Limpiamos los campos propios de persona natural para evitar filtros residuales
      setFiltros((prev) => ({
        ...prev,
        apellido_materno: '',
        primer_nombre: '',
      }));
    }
  };

  const buscar = async (pagina = 1, currentFilters = null, refineOverride = null) => {
    const searchFilters = currentFilters || filtros;
    const refineTerm = refineOverride !== null ? refineOverride : filtroRefinar;
    const hayFiltro = Object.values(searchFilters).some((v) => typeof v === 'string' && v.trim() !== '');
    if (!hayFiltro) {
      setError(tipoPersona === 'empresa' ? 'Ingresa la Razón Social o nombre de la empresa.' : 'Ingresa al menos un apellido o nombre.');
      return;
    }

    if (!executeRecaptcha) {
      setError('ReCAPTCHA no está listo aún.');
      return;
    }
    const recaptchaToken = await executeRecaptcha('indice_search');

    setError('');
    setCargando(true);
    setBuscado(true);

    try {
      const params = { 
        ...searchFilters, 
        page: pagina, 
        per_page: POR_PAGINA,
        ...(refineTerm.trim() ? { refinar: refineTerm.trim() } : {})
      };
      const { data } = await api.get('/buscador/propiedades', { params, headers: { 'X-Recaptcha-Token': recaptchaToken } });
      console.log('Respuesta del servidor datos:', data);

      const meta = data.meta || {};
      const total = data.total ?? meta.total ?? 0;
      const lastPage = data.last_page ?? meta.last_page ?? 1;
      const currentPage = data.current_page ?? meta.current_page ?? pagina;
      const totalIntervinientes = meta.total_intervinientes ?? null;

      setResultados({
        ...data,
        total,
        last_page: lastPage,
        current_page: currentPage,
        total_intervinientes: totalIntervinientes,
      });
      setPaginaActual(currentPage);
    } catch (err) {
      setError(
        err.response?.data?.message || 'Error al realizar la búsqueda. Intenta nuevamente.'
      );
      setResultados(null);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const p = params.get('apellido_paterno') || '';
    const m = params.get('apellido_materno') || '';
    const n = params.get('primer_nombre') || '';
    const t = params.get('tipo_registro') || '';

    if (p || m || n || t) {
      // Si solo viene apellido_paterno sin apellido_materno ni primer_nombre, asumimos que puede ser empresa
      if (p && !m && !n) {
        setTipoPersona('empresa');
      }
      setFiltros({ apellido_paterno: p, apellido_materno: m, primer_nombre: n, tipo_registro: t });
      if (executeRecaptcha && !buscado && !cargando) {
        buscar(1, { apellido_paterno: p, apellido_materno: m, primer_nombre: n, tipo_registro: t });
      }
    }
  }, [location.search, executeRecaptcha]);

  const limpiar = () => {
    setFiltros(FILTROS_VACIOS);
    setFiltroRefinar('');
    setResultados(null);
    setError('');
    setBuscado(false);
    setPaginaActual(1);
  };

  const handleRefinarSubmit = (e) => {
    e.preventDefault();
    buscar(1);
  };

  const handleClearRefinar = () => {
    setFiltroRefinar('');
    buscar(1, null, '');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') buscar(1);
  };

  const imprimirPaginaActual = () => {
    if (!resultados || !resultados.data || resultados.data.length === 0) return;
    const natObj = opcionesNaturalezas.find(o => String(o.value) === String(filtros.naturaleza_id));
    handleImprimirIndice({
      registros: resultados.data,
      criterios: {
        tipoPersona,
        filtros,
        naturalezaDescripcion: natObj?.label || '',
        filtroRefinar,
        totalInscripciones: resultados.total,
        totalIntervinientes: resultados.total_intervinientes
      },
      usuario: user,
      esBúsquedaCompleta: false,
      paginaActual: resultados.current_page || paginaActual,
      totalPaginas: resultados.last_page || 1
    });
    setShowPrintModal(false);
  };

  const imprimirTodo = async () => {
    if (!resultados) return;
    setImprimiendoTodo(true);
    try {
      const params = {
        ...filtros,
        page: 1,
        per_page: Math.min(resultados.total || 500, 1000),
        ...(filtroRefinar.trim() ? { refinar: filtroRefinar.trim() } : {})
      };
      
      const { data } = await api.get('/buscador/propiedades', { params });
      const todosRegistros = data.data || [];
      const natObj = opcionesNaturalezas.find(o => String(o.value) === String(filtros.naturaleza_id));

      handleImprimirIndice({
        registros: todosRegistros,
        criterios: {
          tipoPersona,
          filtros,
          naturalezaDescripcion: natObj?.label || '',
          filtroRefinar,
          totalInscripciones: resultados.total,
          totalIntervinientes: resultados.total_intervinientes
        },
        usuario: user,
        esBúsquedaCompleta: true,
        paginaActual: 1,
        totalPaginas: 1
      });
      setShowPrintModal(false);
    } catch (err) {
      console.error('Error al descargar registros para impresión:', err);
      toast.error('Error al generar el informe completo para impresión.');
    } finally {
      setImprimiendoTodo(false);
    }
  };

  const handleClickImprimir = () => {
    if (!resultados || !resultados.data || resultados.data.length === 0) {
      toast.error('No hay registros disponibles para imprimir.');
      return;
    }
    if (resultados.last_page <= 1) {
      imprimirPaginaActual();
    } else {
      setShowPrintModal(true);
    }
  };

  return (
    <div className="container" style={{ padding: '2rem clamp(16px,4vw,48px)' }}>

      {/* Encabezado de sección */}
      <div className="animate-fade-in">
        <h2 className="section-title">Índice de Propiedades</h2>
        <span className="accent-line" />
        <p className="section-subtitle">
          Busca titulares e inscripciones registradas en el Conservador de Bienes Raíces.
        </p>
      </div>

      {/* Formulario de búsqueda */}
      <div className="glass-card animate-fade-in" style={{ marginBottom: '2rem' }}>
        
        {/* Toggle Selector Persona Natural vs Empresa */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
          <button
            type="button"
            onClick={() => handleSwitchTipo('persona')}
            style={{
              padding: '0.5rem 1.1rem',
              borderRadius: '8px',
              fontSize: '0.88rem',
              fontWeight: tipoPersona === 'persona' ? '700' : '500',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              background: tipoPersona === 'persona' ? 'var(--primary, #003366)' : '#ffffff',
              color: tipoPersona === 'persona' ? '#ffffff' : 'var(--text-main)',
              border: tipoPersona === 'persona' ? '1px solid var(--primary, #003366)' : '1px solid #cbd5e1',
            }}
          >
            <User size={16} /> Persona Natural
          </button>
          <button
            type="button"
            onClick={() => handleSwitchTipo('empresa')}
            style={{
              padding: '0.5rem 1.1rem',
              borderRadius: '8px',
              fontSize: '0.88rem',
              fontWeight: tipoPersona === 'empresa' ? '700' : '500',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              background: tipoPersona === 'empresa' ? 'var(--primary, #003366)' : '#ffffff',
              color: tipoPersona === 'empresa' ? '#ffffff' : 'var(--text-main)',
              border: tipoPersona === 'empresa' ? '1px solid var(--primary, #003366)' : '1px solid #cbd5e1',
            }}
          >
            <Building2 size={16} /> Empresa / Institución
          </button>
        </div>

        {/* Campos dinámicos según tipo */}
        {tipoPersona === 'empresa' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label htmlFor="apellido_paterno" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Building2 size={15} color="var(--primary, #003366)" />
                Razón Social / Nombre de la Empresa
              </label>
              <input
                id="apellido_paterno"
                name="apellido_paterno"
                value={filtros.apellido_paterno}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Ej: Empresa SpA, Inversiones Ltda., Inmobiliaria..."
                autoFocus
              />
              <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px', display: 'block' }}>
                Ingresa el nombre o razón social de la sociedad, empresa o inmobiliaria.
              </span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '1rem',
            }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="tipo_registro">Tipo de Actuación</label>
                <select
                  id="tipo_registro"
                  name="tipo_registro"
                  value={filtros.tipo_registro}
                  onChange={handleChange}
                >
                  <option value="">Todos los registros</option>
                  <option value="propiedad">Propiedad</option>
                  <option value="hipotecas">Hipotecas</option>
                  <option value="prohibiciones">Prohibiciones</option>
                  <option value="aguas">Propiedad de Aguas</option>
                  <option value="hipotecas_aguas">Hipotecas de Aguas</option>
                  <option value="prohibiciones_aguas">Prohibiciones de Aguas</option>
                </select>
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="naturaleza_id">Naturaleza del Acto</label>
                <Select
                  id="naturaleza_id"
                  inputId="naturaleza_id_input"
                  name="naturaleza_id"
                  placeholder="Buscar naturaleza (ej: Compraventa...)"
                  isClearable
                  isSearchable
                  options={opcionesNaturalezas}
                  value={opcionesNaturalezas.find(o => String(o.value) === String(filtros.naturaleza_id)) || null}
                  onChange={(opt) => setFiltros(prev => ({ ...prev, naturaleza_id: opt ? opt.value : '' }))}
                  noOptionsMessage={() => "No se encontraron naturalezas"}
                  styles={selectNaturalezaStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
            {/* Fila 1: Datos de la persona */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
            }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="apellido_paterno">Apellido Paterno</label>
                <input
                  id="apellido_paterno"
                  name="apellido_paterno"
                  value={filtros.apellido_paterno}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  placeholder="González"
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="apellido_materno">Apellido Materno</label>
                <input
                  id="apellido_materno"
                  name="apellido_materno"
                  value={filtros.apellido_materno}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Muñoz"
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="primer_nombre">Primer Nombre</label>
                <input
                  id="primer_nombre"
                  name="primer_nombre"
                  value={filtros.primer_nombre}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Carlos"
                />
              </div>
            </div>

            {/* Fila 2: Clasificación registral */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '1rem',
            }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="tipo_registro">Tipo de Actuación</label>
                <select
                  id="tipo_registro"
                  name="tipo_registro"
                  value={filtros.tipo_registro}
                  onChange={handleChange}
                >
                  <option value="">Todos los registros</option>
                  <option value="propiedad">Propiedad</option>
                  <option value="hipotecas">Hipotecas</option>
                  <option value="prohibiciones">Prohibiciones</option>
                  <option value="aguas">Propiedad de Aguas</option>
                  <option value="hipotecas_aguas">Hipotecas de Aguas</option>
                  <option value="prohibiciones_aguas">Prohibiciones de Aguas</option>
                </select>
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="naturaleza_id_persona">Naturaleza del Acto</label>
                <Select
                  id="naturaleza_id_persona"
                  inputId="naturaleza_id_persona_input"
                  name="naturaleza_id"
                  placeholder="Buscar naturaleza (ej: Compraventa...)"
                  isClearable
                  isSearchable
                  options={opcionesNaturalezas}
                  value={opcionesNaturalezas.find(o => String(o.value) === String(filtros.naturaleza_id)) || null}
                  onChange={(opt) => setFiltros(prev => ({ ...prev, naturaleza_id: opt ? opt.value : '' }))}
                  noOptionsMessage={() => "No se encontraron naturalezas"}
                  styles={selectNaturalezaStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
              </div>
            </div>
          </div>
        )}

        {/* Acciones */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={limpiar}
            title="Limpiar filtros"
          >
            <RotateCcw size={15} /> Limpiar
          </button>
          <button
            className="btn btn-primary"
            onClick={() => buscar(1)}
            disabled={cargando}
          >
            {cargando
              ? <><span className="spinner" /> Buscando...</>
              : <><Search size={16} /> Buscar</>
            }
          </button>
        </div>

        {error && (
          <div className="alert alert-warning" style={{ marginTop: '1rem' }}>
            ⚠️ {error}
          </div>
        )}
      </div>

      {/* Resultados */}
      {buscado && !cargando && resultados && (
        <div className="animate-fade-in">
          {/* Contador y Botón de Impresión para Usuarios Autenticados */}
          <div style={{ 
            marginBottom: '0.85rem', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            flexWrap: 'wrap', 
            gap: '0.75rem',
            background: user ? 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' : 'transparent',
            padding: user ? '0.65rem 1rem' : '0',
            borderRadius: '10px',
            border: user ? '1px solid #e2e8f0' : 'none',
            boxShadow: user ? '0 1px 4px rgba(0,0,0,0.02)' : 'none'
          }}>
            <p className="text-muted" style={{ margin: 0, fontSize: '0.88rem' }}>
              Se encontraron <strong style={{ color: 'var(--primary, #003366)' }}>
                {resultados.total}
              </strong> inscripciones
              {resultados.total_intervinientes ? (
                <span> (<strong style={{ color: 'var(--primary, #003366)' }}>{resultados.total_intervinientes}</strong> intervenciones encontradas)</span>
              ) : null}.
              {' '}Mostrando página <strong style={{ color: 'var(--primary, #003366)' }}>{resultados.current_page}</strong> de <strong style={{ color: 'var(--primary, #003366)' }}>{resultados.last_page}</strong>.
            </p>

            {/* BOTÓN IMPRIMIR: SOLO ACCESIBLE PARA USUARIOS AUTENTICADOS */}
            {user && resultados.data.length > 0 && (
              <button
                type="button"
                onClick={handleClickImprimir}
                className="btn btn-secondary btn-sm"
                title="Generar informe registral para imprimir o guardar como PDF"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#ffffff',
                  color: '#003366',
                  border: '1.5px solid #003366',
                  fontWeight: 700,
                  fontSize: '0.84rem',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(0, 51, 102, 0.08)',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#eff6ff';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(0, 51, 102, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 51, 102, 0.08)';
                }}
              >
                <Printer size={15} strokeWidth={2.2} />
                <span>Imprimir Resultados</span>
                {resultados.last_page > 1 && (
                  <span style={{
                    background: '#003366',
                    color: '#ffffff',
                    fontSize: '0.68rem',
                    padding: '1px 6px',
                    borderRadius: '10px',
                    marginLeft: '2px',
                    fontWeight: 700
                  }}>
                    {resultados.total}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Barra de Refinamiento Rápido */}
          <div 
            className="glass-card" 
            style={{ 
              marginBottom: '1.25rem', 
              padding: '0.75rem 1rem',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              gap: '0.75rem',
              flexWrap: 'wrap',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '10px'
            }}
          >
            <form 
              onSubmit={handleRefinarSubmit} 
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: '260px' }}
            >
              <div style={{ position: 'relative', flex: 1 }}>
                <Search 
                  size={16} 
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary, #003366)' }} 
                />
                <input
                  type="text"
                  value={filtroRefinar}
                  onChange={(e) => setFiltroRefinar(e.target.value)}
                  placeholder="Filtrar en estos resultados (calle, comuna, foja, año, rol, etc.)..."
                  style={{
                    width: '100%',
                    padding: '0.5rem 2.2rem 0.5rem 2.4rem',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.88rem',
                    outline: 'none',
                    background: '#f8fafc',
                    color: 'var(--text-main)',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--primary, #003366)'; e.target.style.background = '#fff'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#cbd5e1'; e.target.style.background = '#f8fafc'; }}
                />
                {filtroRefinar && (
                  <button
                    type="button"
                    onClick={handleClearRefinar}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#94a3b8',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title="Borrar filtro"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={cargando}
                style={{ padding: '0.5rem 1rem', whiteSpace: 'nowrap' }}
              >
                Filtrar
              </button>
            </form>

            {filtroRefinar && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span className="badge badge-copper" style={{ fontSize: '0.78rem' }}>
                  Filtro: "{filtroRefinar}"
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleClearRefinar}
                  style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem' }}
                >
                  ✕ Quitar
                </button>
              </div>
            )}
          </div>

          {resultados.data.length > 0 ? (
            <>
              {/* Tabla */}
              <div className="table-wrapper" style={{ marginBottom: '1.5rem' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Registro</th>
                      <th>Naturaleza del Acto</th>
                      <th>Foja</th>
                      <th>Núm.</th>
                      <th>Año</th>
                      <th>Propiedad</th>
                      <th>Comuna</th>
                      {isStaff() && <th>Acciones</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {resultados.data.map((registro) => (
                      <tr key={registro.id}>
                        <td>
                          {registro.intervinientes && registro.intervinientes.length > 0 ? (
                            registro.intervinientes.map((inv, idx) => (
                              <div key={idx} style={{ paddingBottom: idx !== registro.intervinientes.length - 1 ? '4px' : '0' }}>
                                <strong>
                                  {[inv.nombres, inv.apellido_paterno, inv.apellido_materno].filter(Boolean).join(' ')}
                                </strong>
                                <span style={{ fontSize: '0.75rem', marginLeft: '6px', opacity: 0.7 }}>({inv.rol})</span>
                              </div>
                            ))
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <span className="badge badge-copper" style={{ textTransform: 'capitalize' }}>
                            {registro.tipo_registro}
                          </span>
                        </td>
                        <td>
                          {registro.naturaleza?.descripcion || registro.naturaleza_acto ? (
                            <span
                              className="badge"
                              style={{
                                background: '#eff6ff',
                                color: '#1e40af',
                                border: '1px solid #bfdbfe',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.2px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title={registro.naturaleza?.codigo_antiguo ? `Código: ${registro.naturaleza.codigo_antiguo}` : ''}
                            >
                              {registro.naturaleza?.descripcion || registro.naturaleza_acto}
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-muted">{registro.foja_inicio ?? '—'}</td>
                        <td className="text-muted">{registro.numero ?? '—'}</td>
                        <td className="text-muted">{registro.anio ?? '—'}</td>
                        <td className="text-muted">
                          {registro.inmueble ? registro.inmueble.calle : '—'}
                        </td>
                        <td className="text-muted">   {registro.inmueble ? registro.inmueble.comuna : '—'}</td>
                        {isStaff() && (
                          <td>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setModalRegistro(registro)}
                              title="Gestionar Documentos"
                              style={{ padding: '0.4rem', whiteSpace: 'nowrap' }}
                            >
                              <UploadCloud size={16} /> Docs
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {resultados.last_page > 1 && (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => buscar(paginaActual - 1)}
                    disabled={paginaActual <= 1}
                  >
                    ← Anterior
                  </button>

                  {/* Primera página si está lejos */}
                  {paginaActual > 3 && (
                    <>
                      <button
                        className="btn btn-sm"
                        style={{
                          background: '#ffffff',
                          color: 'var(--primary, #003366)',
                          border: '1px solid #cbd5e1',
                          fontWeight: '600',
                          minWidth: '36px',
                          height: '34px',
                        }}
                        onClick={() => buscar(1)}
                      >
                        1
                      </button>
                      {paginaActual > 4 && <span style={{ color: 'var(--primary, #003366)', padding: '0 4px', fontWeight: 'bold' }}>...</span>}
                    </>
                  )}

                  {/* Páginas cercanas */}
                  {Array.from({ length: resultados.last_page }, (_, i) => i + 1)
                    .filter((p) => Math.abs(p - paginaActual) <= 2)
                    .map((p) => (
                      <button
                        key={p}
                        className="btn btn-sm"
                        style={{
                          background: p === paginaActual ? 'var(--primary, #003366)' : '#ffffff',
                          color: p === paginaActual ? '#ffffff' : 'var(--primary, #003366)',
                          border: p === paginaActual ? '1px solid var(--primary, #003366)' : '1px solid #cbd5e1',
                          fontWeight: p === paginaActual ? '700' : '600',
                          minWidth: '36px',
                          height: '34px',
                        }}
                        onClick={() => buscar(p)}
                      >
                        {p}
                      </button>
                    ))}

                  {/* Última página si está lejos */}
                  {paginaActual < resultados.last_page - 2 && (
                    <>
                      {paginaActual < resultados.last_page - 3 && <span style={{ color: 'var(--primary, #003366)', padding: '0 4px', fontWeight: 'bold' }}>...</span>}
                      <button
                        className="btn btn-sm"
                        style={{
                          background: '#ffffff',
                          color: 'var(--primary, #003366)',
                          border: '1px solid #cbd5e1',
                          fontWeight: '600',
                          minWidth: '36px',
                          height: '34px',
                        }}
                        onClick={() => buscar(resultados.last_page)}
                      >
                        {resultados.last_page}
                      </button>
                    </>
                  )}

                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => buscar(paginaActual + 1)}
                    disabled={paginaActual >= resultados.last_page}
                  >
                    Siguiente →
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="glass-card" style={{ textAlign: 'center', padding: '3rem' }}>
              <FileText size={48} style={{ color: 'var(--gray-600)', marginBottom: '1rem' }} />
              <p className="text-muted" style={{ marginBottom: filtroRefinar ? '0.75rem' : 0 }}>
                {filtroRefinar 
                  ? `No se encontraron registros coincidentes con "${filtroRefinar}" dentro de esta búsqueda.`
                  : 'No se encontraron registros con los criterios ingresados.'}
              </p>
              {filtroRefinar && (
                <button 
                  className="btn btn-secondary btn-sm" 
                  onClick={handleClearRefinar}
                >
                  ✕ Quitar filtro "{filtroRefinar}"
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {modalRegistro && (
        <IndiceDocumentosModal 
          indice={modalRegistro} 
          onClose={() => setModalRegistro(null)} 
        />
      )}

      {/* Modal de Selección de Alcance de Impresión */}
      {showPrintModal && (
        <div 
          className="modal-overlay"
          onClick={() => !imprimiendoTodo && setShowPrintModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.25rem',
            zIndex: 1000
          }}
        >
          <div 
            className="modal-content animate-fade-in"
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: 520,
              width: '100%',
              background: '#ffffff',
              borderRadius: '16px',
              padding: '1.5rem',
              boxShadow: '0 20px 40px -10px rgba(0, 51, 102, 0.3)',
              border: '1px solid #e2e8f0'
            }}
          >
            {/* Header Modal */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.85rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ background: '#eff6ff', color: '#003366', padding: '8px', borderRadius: '10px' }}>
                  <Printer size={22} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#003366', fontWeight: 800 }}>
                    Opciones de Impresión de Resultados
                  </h3>
                  <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                    Total: {resultados?.total || 0} inscripciones encontradas ({resultados?.last_page || 1} páginas)
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowPrintModal(false)}
                disabled={imprimiendoTodo}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}
              >
                <X size={18} />
              </button>
            </div>

            <p style={{ margin: '0 0 1.25rem', fontSize: '0.88rem', color: '#334155', lineHeight: 1.45 }}>
              Selecciona cómo deseas generar el informe registral institucional para impresión en papel o guardado en formato PDF:
            </p>

            {/* Opciones de Impresión */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginBottom: '1.5rem' }}>
              {/* Opción 1: Página Actual */}
              <div 
                onClick={imprimirPaginaActual}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '1rem', borderRadius: '12px',
                  border: '1.5px solid #cbd5e1', background: '#f8fafc',
                  cursor: 'pointer', transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#003366'; e.currentTarget.style.background = '#eff6ff'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#f8fafc'; }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#003366' }}>
                    📄 Imprimir Página Actual ({resultados?.data?.length || 0} registros)
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                    Genera el informe con los {resultados?.data?.length || 0} registros que estás visualizando actualmente en la página {resultados?.current_page || 1}.
                  </div>
                </div>
                <span style={{
                  fontSize: '0.8rem', fontWeight: 700, color: '#003366',
                  background: '#ffffff', border: '1px solid #cbd5e1',
                  padding: '6px 14px', borderRadius: '8px', flexShrink: 0, marginLeft: 12
                }}>
                  Imprimir Página
                </span>
              </div>

              {/* Opción 2: Búsqueda Completa */}
              <div 
                onClick={!imprimiendoTodo ? imprimirTodo : undefined}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '1rem', borderRadius: '12px',
                  border: '1.5px solid #003366', background: '#f0fdf4',
                  cursor: imprimiendoTodo ? 'wait' : 'pointer', transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => { if (!imprimiendoTodo) { e.currentTarget.style.borderColor = '#002244'; e.currentTarget.style.background = '#ecfdf5'; } }}
                onMouseLeave={e => { if (!imprimiendoTodo) { e.currentTarget.style.borderColor = '#003366'; e.currentTarget.style.background = '#f0fdf4'; } }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#166534' }}>
                    📑 Imprimir Búsqueda Completa ({resultados?.total || 0} inscripciones)
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#15803d', marginTop: 2 }}>
                    Consolida todas las {resultados?.last_page || 1} páginas de resultados en un único documento continuo.
                  </div>
                </div>
                <button
                  type="button"
                  disabled={imprimiendoTodo}
                  style={{
                    fontSize: '0.82rem', fontWeight: 700, color: '#ffffff',
                    background: imprimiendoTodo ? '#94a3b8' : '#16a34a',
                    border: 'none', padding: '8px 16px', borderRadius: '8px',
                    cursor: imprimiendoTodo ? 'wait' : 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 12
                  }}
                >
                  {imprimiendoTodo ? (
                    <>
                      <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                      <span>Descargando...</span>
                    </>
                  ) : (
                    'Imprimir Todo'
                  )}
                </button>
              </div>
            </div>

            {/* Footer Modal */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowPrintModal(false)}
                disabled={imprimiendoTodo}
                className="btn btn-secondary btn-sm"
                style={{ borderRadius: '8px', padding: '8px 18px', fontWeight: 600 }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
